import { Fragment, useState, useEffect } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { KeenIcon } from '@/components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useGetArticleByIdQuery,
  useGetArticleVersionsQuery,
} from '@/services/ArticleService';
import { languageOptions } from '@/types/cms.types';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/store/store';
import { setLanguage } from '@/store/authSlice';
import { toAbsoluteUrl } from '@/utils';
import DOMPurify from 'dompurify';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDate } from '@/utils/Date';

const ArticleView = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const language = useSelector((state: RootState) => state.auth.language);
  const [searchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const versionParam = searchParams.get('version');

  const { data: article, isLoading } = useGetArticleByIdQuery(id as string, {
    skip: !id || !!versionParam,
  });

  const { data: versionsData, isLoading: isLoadingVersions } =
    useGetArticleVersionsQuery(id as string, {
      skip: !id || !versionParam,
    });

  // Get the specific version from versions data
  const versionArticle =
    versionParam && versionsData?.data
      ? versionsData.data.find((v) => v.version === parseInt(versionParam))
      : null;

  // Use version data if available, otherwise use regular article data
  const currentArticle = versionArticle || article;

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setLanguage(e.target.value));
  };

  const [displayContent, setDisplayContent] = useState({
    title: '',
    body: '',
  });

  useEffect(() => {
    if (currentArticle) {
      // Check if translation exists for current language
      const translation = currentArticle.translations?.[language];

      if (translation) {
        // Use translated content
        setDisplayContent({
          title: translation.title || currentArticle.title,
          body: translation.body || currentArticle.body,
        });
      } else {
        // Use original content
        setDisplayContent({
          title: currentArticle.title,
          body: currentArticle.body,
        });
      }
    }
  }, [currentArticle, language]);

  if (isLoading || isLoadingVersions) {
    return <div className="p-10 text-center">Loading article...</div>;
  }

  if (!currentArticle) {
    return (
      <div className="p-10 text-center">
        <h2 className="text-2xl font-bold mb-4">Article not found</h2>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/article-management')}
        >
          Back to Articles
        </button>
      </div>
    );
  }

  return (
    <Fragment>
      <section aria-labelledby="article-view-heading">
        {currentLayout?.name === 'demo1-layout' && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle
                  id="article-view-heading"
                  text={
                    versionParam
                      ? `View Article - Version ${versionParam}`
                      : 'View Article'
                  }
                />
                <ToolbarDescription>
                  {versionParam
                    ? `Viewing version ${versionParam} of article`
                    : 'Previewing CMS article content'}
                </ToolbarDescription>
              </ToolbarHeading>
              <ToolbarActions>
                <div className="relative">
                  <select
                    value={language}
                    onChange={handleLanguageChange}
                    aria-label="Language for the article"
                    className="select select-bordered pl-8 pr-8 h-10 leading-normal"
                  >
                    {languageOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <img
                    src="/media/flags/globe.svg"
                    alt="Globe"
                    className="absolute dark:brightness-0 dark:invert contrast:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
                  />
                </div>
                <button
                  className="btn btn-sm btn-light h-10"
                  onClick={() =>
                    navigate(
                      versionParam
                        ? `/article-management/${id}/versions`
                        : '/article-management'
                    )
                  }
                >
                  Back to {versionParam ? 'Versions' : 'List'}
                </button>
                {!versionParam &&
                  hasPermission('article_management_update') && (
                    <button
                      className="btn btn-sm btn-primary h-10"
                      onClick={() => navigate(`/article-management/edit/${id}`)}
                    >
                      <KeenIcon icon="pencil" className="text-white" />
                      Edit Article
                    </button>
                  )}
              </ToolbarActions>
            </Toolbar>
          </Container>
        )}

        <div className="container-fixed max-w-4xl mx-auto">
          <div className="card overflow-hidden">
            {/* Article Header Image */}
            <h1 className="text-4xl p-10 font-bold text-gray-900 mb-0 leading-tight">
              {displayContent.title}
            </h1>
            <div className="relative h-[400px] w-full">
              <img
                src={
                  currentArticle.coverImageUrl
                    ? toAbsoluteUrl(currentArticle.coverImageUrl)
                    : '/media/avatars/harbor-default.jpg'
                }
                className="w-full h-full object-cover"
                alt={currentArticle.title}
              />
              {currentArticle.category && (
                <div className="absolute top-6 right-6 bg-black/70 text-white px-4 py-1.5 rounded-full text-sm font-medium backdrop-blur-md">
                  {currentArticle.category}
                </div>
              )}
              <div className="absolute bottom-6 left-6 bg-gray-100 px-4 py-1.5 rounded-lg text-sm font-medium text-gray-700 backdrop-blur-md">
                {formatDisplayDate(currentArticle.createdAt)}
              </div>
            </div>

            <div className="card-body p-10">
              {/* Article Content (Rich Text) */}
              <div
                className="prose prose-lg max-w-none text-gray-800 leading-relaxed
                  prose-headings:text-gray-900 prose-strong:text-gray-900 prose-headings:font-bold
                  prose-a:text-primary-active hover:prose-a:text-primary-active contrast:prose-a:text-blue-300 contrast:hover:prose-a:text-blue-200 prose-blockquote:text-gray-700 
                  prose-code:text-gray-900 prose-ol:text-gray-800 prose-ul:text-gray-800 
                  prose-pre:bg-gray-100 prose-pre:text-gray-700 prose-pre:border prose-pre:border-gray-100 prose-pre:rounded prose-pre:p-4
                  prose-p:mb-6 prose-li:mb-2
                  prose-img:rounded-xl prose-img:shadow-lg"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(displayContent.body),
                }}
              />
            </div>
          </div>
        </div>
      </section>
    </Fragment>
  );
};

export { ArticleView };
